import {
  Typography,
  Box,
  Container,
  Grid,
  Card,
  Divider,
  Theme,
} from "@mui/material";
import { motion } from "framer-motion";
import React from "react";
import ContentSection from "../ContentSection";
import { useTranslation } from "react-i18next";

interface ParentContentProps {
  theme: Theme;
  getBannerImage: () => string;
}

const ParentContent: React.FC<ParentContentProps> = ({
  theme,
  getBannerImage,
}) => {
  const { t } = useTranslation();

  return (
    <Container
      maxWidth={false}
      sx={{
        py: 4,
        px: { xs: 2, sm: 3, md: 4, lg: 5, xl: 6 },
        maxWidth: "100%",
        mx: "auto",
      }}
    >
      {/* Main card */}
      <Card
        elevation={2}
        sx={{
          borderRadius: 3,
          overflow: "hidden",
          bgcolor: "white",
          maxWidth: "100%",
          mx: "auto",
        }}
      >
        {/* Banner image */}
        <Box
          sx={{
            position: "relative",
            width: "100%",
            height: { xs: 250, sm: 350, md: 400, lg: 450, xl: 500 },
          }}
        >
          <img
            src={getBannerImage()}
            alt="Parent Portal"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "center",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              width: "100%",
              background:
                "linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 100%)",
              p: { xs: 3, sm: 4, md: 5 },
            }}
          >
            <Typography
              variant="h3"
              fontWeight={700}
              color="white"
              gutterBottom
              sx={{ fontSize: { xs: "1.75rem", sm: "2.25rem", md: "2.75rem" } }}
            >
              Welcome to the CYPMS Parent Portal
            </Typography>
            <Typography
              variant="h5"
              fontWeight={500}
              color="white"
              sx={{ fontSize: { xs: "1rem", sm: "1.25rem", md: "1.5rem" } }}
            >
              Stay Connected, Stay Informed
            </Typography>
          </Box>
        </Box>

        {/* Content */}
        <Box sx={{ p: { xs: 3, sm: 4, md: 5 } }}>
          <ParentIntroSection />
          <Divider sx={{ my: 4 }} />
          <ParentFeaturesSection theme={theme} />
          <Divider sx={{ my: 4 }} />
          <ParentResourcesSection theme={theme} />
        </Box>
      </Card>
    </Container>
  );
};

// Parent intro section
const ParentIntroSection: React.FC = () => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.7 }}
    >
      <Typography
        variant="h4"
        fontWeight={700}
        gutterBottom
        color="primary"
        sx={{ fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2rem" } }}
      >
        What Is CYPMS?
      </Typography>
      <Typography variant="body1" sx={{ mb: 3 }} color="text.secondary">
        The CODICE Youth Program Management System (CYPMS) is a centralized
        digital platform that supports DC's youth employment initiatives like
        MBSYEP. The Parent Portal gives guardians secure access to monitor and
        support their child's participation in these programs.
      </Typography>
      <Typography variant="body1" sx={{ mb: 4 }} color="text.secondary">
        From approving consent forms to viewing work history and receiving
        important updates—CYPMS ensures you stay connected to your child's
        progress every step of the way.
      </Typography>
    </motion.div>
  );
};

interface SectionWithThemeProps {
  theme: Theme;
}

// Parent features section
const ParentFeaturesSection: React.FC<SectionWithThemeProps> = ({ theme }) => {
  return (
    <>
      <Typography
        variant="h4"
        fontWeight={700}
        gutterBottom
        color="primary"
        sx={{ fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2rem" } }}
      >
        Things You Should Know About CYPMS
      </Typography>
      <Typography variant="body1" sx={{ mb: 3 }} color="text.secondary">
        Here's what you can do once you're invited and logged in:
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6} lg={4}>
          <ContentSection
            theme={theme}
            title="Approve Parental Consent Requests"
            content="Easily approve or decline parental consent forms submitted by your child as part of their program enrollment."
            delay={0.1}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <ContentSection
            theme={theme}
            title="View Linked Youth Profiles"
            content="See a list of all children who have identified you as their parent or guardian, with access to their program participation details."
            delay={0.2}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <ContentSection
            theme={theme}
            title="Track Enrollment Status"
            content="Follow your child's enrollment progress, including completion of tax forms, orientation, and certification quizzes."
            delay={0.3}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <ContentSection
            theme={theme}
            title="View Timesheet and Attendance"
            content="Monitor your child's logged hours, attendance, and timesheet submission status across program weeks."
            delay={0.4}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <ContentSection
            theme={theme}
            title="View Payment History"
            content="Access summaries of your child's earnings, pay periods, and payment dates—helping you guide their financial understanding."
            delay={0.5}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <ContentSection
            theme={theme}
            title="Receive Notifications and Alerts"
            content="Stay informed with real-time updates from DOES, program staff, or hosts regarding job assignments, issues, or upcoming events."
            delay={0.6}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <ContentSection
            theme={theme}
            title="Use AI Assistant for Support"
            content="Have a question about the program, documents, or timelines? Ask our virtual assistant for instant guidance—available 24/7."
            delay={0.7}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <ContentSection
            theme={theme}
            title="Secure Account Access"
            content="Your account is protected by multi-step authentication, ensuring your family's information stays safe and confidential."
            delay={0.8}
          />
        </Grid>
      </Grid>
    </>
  );
};

// Parent resources section
const ParentResourcesSection: React.FC<SectionWithThemeProps> = ({ theme }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.9 }}
    >
      <Card
        elevation={1}
        sx={{
          p: 4,
          borderRadius: 2,
          bgcolor: theme.palette.grey[50],
          border: `1px solid ${theme.palette.grey[200]}`,
        }}
      >
        <Typography variant="h5" fontWeight={700} gutterBottom color="primary">
          What Happens After Your Child Registers?
        </Typography>
        <Box component="ul" sx={{ pl: 4, mb: 0 }}>
          <Box component="li" sx={{ mb: 1, color: "text.secondary" }}>
            You'll receive an email invitation to create your CYPMS account
          </Box>
          <Box component="li" sx={{ mb: 1, color: "text.secondary" }}>
            Log in to approve the parental consent form
          </Box>
          <Box component="li" sx={{ mb: 1, color: "text.secondary" }}>
            Monitor their progress through orientation, onboarding, and
            placement
          </Box>
          <Box component="li" sx={{ mb: 1, color: "text.secondary" }}>
            View timesheets and pay details as the program begins
          </Box>
          <Box component="li" sx={{ mb: 0, color: "text.secondary" }}>
            Stay involved with updates, messages, and support tools
          </Box>
        </Box>
      </Card>
    </motion.div>
  );
};

export default ParentContent;
